import {
  EuRelabelCheckIn,
  EuRelabelCheckInFormData,
  EuRelabelCheckInProductRow,
  PurchaseOrder,
  TransferOrder,
  PostEuRelabelCheckInRequestBody
} from "src/types";
import { PostTransferOrderEuRelabelCheckInRequestBody } from "src/types/api/euRelabelCheckIns/postTransferOrderEuRelabelCheckIn";
import { hasWarehouseSplit, splitByWarehouseRatio, resolveRelabellingFlags } from "src/helpers";

export class EuRelabelCheckInMapper {
  public static initEuRelabelCheckInFormData(): EuRelabelCheckInFormData {
    return {
      products: []
    };
  }

  public static euRelabelCheckInFormDataFromPurchaseOrder(
    purchaseOrder: PurchaseOrder,
    priorCheckIns: EuRelabelCheckIn[] = []
  ): EuRelabelCheckInFormData {
    const prevReceivedByProductId = new Map<string, number>();
    const prevShortageByProductId = new Map<string, number>();
    for (const checkIn of priorCheckIns) {
      for (const ri of checkIn.routingInstructions) {
        if (ri.type !== "EU_RELABEL" && ri.type !== "EU_STANDARD") continue;
        for (const p of ri.products) {
          prevReceivedByProductId.set(p.id, (prevReceivedByProductId.get(p.id) ?? 0) + p.quantity);
        }
      }
      for (const s of checkIn.shortages) {
        prevShortageByProductId.set(s.productId, (prevShortageByProductId.get(s.productId) ?? 0) + s.shortageQty);
      }
    }

    const products: EuRelabelCheckInProductRow[] = (purchaseOrder.products ?? []).reduce<EuRelabelCheckInProductRow[]>(
      (acc, product) => {
        const confirmationProduct = purchaseOrder.confirmationDetails?.products?.find(
          (confirmation) => confirmation.productId === product.id
        );
        const confirmedQty = confirmationProduct?.confirmedQty;
        const baseConfirmedOrOrderQty = confirmedQty ?? product.quantity;
        const ukQty = product.ukQuantity ?? 0;
        const euQty = product.euQuantity ?? 0;
        const split = hasWarehouseSplit(product);

        const totalExpectedEu = split
          ? splitByWarehouseRatio(baseConfirmedOrOrderQty, ukQty, euQty).eu
          : baseConfirmedOrOrderQty;

        const confirmedEuQty =
          confirmedQty === undefined
            ? undefined
            : split
              ? splitByWarehouseRatio(confirmedQty, ukQty, euQty).eu
              : confirmedQty;

        const orderQtyEu = split ? euQty : product.quantity;

        if (totalExpectedEu <= 0) {
          return acc;
        }

        const { uk: requiresUkRelabelling, eu: requiresEuRelabelling } = resolveRelabellingFlags(product);
        const prevReceivedQty = prevReceivedByProductId.get(product.id) ?? 0;
        const prevShortageQty = prevShortageByProductId.get(product.id) ?? 0;
        const remaining = Math.max(0, totalExpectedEu - prevReceivedQty);

        acc.push({
          id: product.id,
          name: product.name,
          barcode: product.barcode ?? "",
          skuName: product.skuName ?? "",
          size: product.size,
          orderQty: orderQtyEu,
          ...(confirmedEuQty !== undefined && { confirmedEuQty }),
          totalExpected: totalExpectedEu,
          prevReceivedQty,
          prevShortageQty,
          remaining,
          receivedQty: "",
          requiresUkRelabelling,
          requiresEuRelabelling,
          productDate: "",
          dateType: "",
          lotNumber: ""
        });

        return acc;
      },
      []
    );

    return {
      purchaseOrderId: purchaseOrder.id,
      products
    };
  }

  public static euRelabelCheckInFormDataFromTransferOrder(
    transferOrder: TransferOrder,
    priorCheckIns: EuRelabelCheckIn[] = []
  ): EuRelabelCheckInFormData {
    const prevReceivedByProductId = new Map<string, number>();
    for (const checkIn of priorCheckIns) {
      for (const ri of checkIn.routingInstructions) {
        if (ri.type !== "EU_RELABEL" && ri.type !== "EU_STANDARD") continue;
        for (const p of ri.products) {
          prevReceivedByProductId.set(p.id, (prevReceivedByProductId.get(p.id) ?? 0) + p.quantity);
        }
      }
    }

    const products: EuRelabelCheckInProductRow[] = (transferOrder.products ?? []).reduce<EuRelabelCheckInProductRow[]>(
      (acc, product) => {
        const totalExpectedEu = product.euQuantity ?? product.quantity;
        if (totalExpectedEu <= 0) return acc;

        const { uk: requiresUkRelabelling, eu: requiresEuRelabelling } = resolveRelabellingFlags(product);
        const prevReceivedQty = prevReceivedByProductId.get(product.id) ?? 0;
        const remaining = Math.max(0, totalExpectedEu - prevReceivedQty);

        acc.push({
          id: product.id,
          name: product.name,
          barcode: product.barcode ?? "",
          skuName: product.skuName ?? "",
          size: product.size,
          orderQty: totalExpectedEu,
          totalExpected: totalExpectedEu,
          prevReceivedQty,
          prevShortageQty: 0,
          remaining,
          receivedQty: "",
          requiresUkRelabelling,
          requiresEuRelabelling,
          productDate: "",
          dateType: "",
          lotNumber: ""
        });

        return acc;
      },
      []
    );

    return {
      transferOrderId: transferOrder.id,
      products
    };
  }

  public static euRelabelCheckInRequestBodyFromFormData(
    formData: EuRelabelCheckInFormData
  ): PostEuRelabelCheckInRequestBody {
    if (!formData.purchaseOrderId) {
      throw new Error("purchaseOrderId is required to build a PO EU relabel check-in request body");
    }

    const productEntries = formData.products
      .filter(
        (product) => (product.receivedQty !== "" && Number(product.receivedQty) > 0) || product.shortage !== undefined
      )
      .map((product) => ({
        id: product.id,
        skuName: product.skuName,
        receivedQty: product.receivedQty === "" ? 0 : Number(product.receivedQty),
        ...(product.productDate &&
          product.dateType && { productDate: product.productDate, dateType: product.dateType }),
        ...(product.lotNumber && { lotNumber: product.lotNumber })
      }));

    const shortageEntries = formData.products
      .filter((product) => product.shortage !== undefined)
      .map((product) => ({
        productId: product.id,
        shortageQty: product.shortage!.shortageQty,
        reason: product.shortage!.reason,
        ...(product.shortage!.reasonNote && { reasonNote: product.shortage!.reasonNote })
      }));

    return {
      purchaseOrderId: formData.purchaseOrderId,
      products: productEntries,
      ...(shortageEntries.length > 0 && { shortages: shortageEntries })
    };
  }

  public static transferOrderEuRelabelCheckInRequestBodyFromFormData(
    formData: EuRelabelCheckInFormData
  ): PostTransferOrderEuRelabelCheckInRequestBody {
    if (!formData.transferOrderId) {
      throw new Error("transferOrderId is required to build a TO EU relabel check-in request body");
    }

    const productEntries = formData.products
      .filter((product) => product.receivedQty !== "" && Number(product.receivedQty) > 0)
      .map((product) => ({
        id: product.id,
        skuName: product.skuName,
        receivedQty: Number(product.receivedQty),
        ...(product.productDate &&
          product.dateType && { productDate: product.productDate, dateType: product.dateType }),
        ...(product.lotNumber && { lotNumber: product.lotNumber })
      }));

    return {
      transferOrderId: formData.transferOrderId,
      products: productEntries
    };
  }
}
